// BM-H81 P2 - LPAFormSections.
//
// Ten sections rendered from a static schema authored backwards from
// the OPG PDFs LP1H-08.25 and LP1F-08.25 (V's directive 2026-08-28,
// hand-authored F-* .docx set cancelled). Section labels mirror the
// OPG headings verbatim; plain-English restatements come from LP12
// (08.25) where OPG language is too legalistic for a lay client.
//
// State is a flat object under ``answers.instrument`` that the
// backend feeds directly to the LPA engine's zod schema.
//
// BM-H3414 - the donor section used to write donor.full_name (one
// string) and donor.address (free text). Feeding that shape to the
// vendored engine throws a ZodError on every one of title, first_names,
// last_name and address - confirmed by running it directly. This
// rewrite matches the engine's Donor/Address zod shapes exactly
// (lpa-engine.cjs, search "var Donor =" / "var Address =").
//
// BM-H3669 - every section now opens with an intro (LpaIntro) and every
// question, option and label carries its own tooltip (LpaQuestion, LpaOption),
// from lpa/lpaTooltipRegistry.js. That ticket changed what the client reads and
// nothing about what the sections write: every data-testid, every onChange and
// every value written to the instrument is as it was.

import { useState, useCallback, useEffect } from "react";
import { LpaIntro, LpaOption, LpaQuestion } from "./lpa/LpaQuestion";

let idCounter = 0;
function newId(prefix) {
  idCounter += 1;
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}${idCounter}${rand}`;
}

const pillBase = {
  borderRadius: 999,
  border: "1px solid",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
};

// BM-H3669 - the Yes / No pill pair that used to be copied out in full at every
// yes/no question. Same markup, same test ids (`${testidPrefix}-yes` and
// `${testidPrefix}-no`), same colours.
function YesNo({ testidPrefix, value, onPick, compact = false, capitalize = true, style }) {
  return (
    <div style={{ display: "flex", gap: 8, ...style }}>
      {[["yes", true], ["no", false]].map(([key, val]) => (
        <button
          key={key}
          type="button"
          data-testid={`${testidPrefix}-${key}`}
          onClick={() => onPick(val)}
          style={{
            ...pillBase,
            padding: compact ? "4px 12px" : "6px 14px",
            borderColor: value === val ? "#4338ca" : "#cbd5e1",
            background: value === val ? "#4338ca" : "#fff",
            color: value === val ? "#fff" : "#334155",
            ...(capitalize ? { textTransform: "capitalize" } : {}),
          }}
        >
          {key}
        </button>
      ))}
    </div>
  );
}

// BM-H3414 - shared editor for a person's name, optional date of birth and
// address, used for the donor, attorneys, replacements, people to notify, the
// certificate provider and a "someone else" contact. BM-H3669 gave it a `role`
// (the id prefix, lpa.<role>.<field>) and the few options those callers needed
// so one editor serves them all, and each field now sits in a LpaQuestion.
//
//   role             attorney | replacement | notified | donor | cert_provider |
//                    correspondent. Chooses the tooltip ids.
//   focusPrefix      when set, onFocus reports `${focusPrefix}.<field>` (the
//                    engine path); when not set it reports `${testidPrefix}.<field>`
//                    as it always did for attorneys and replacements.
//   afterLastName    extra fields that sit between last name and the next field
//                    (the donor's other names, a contact's company).
//   addressLines     3 normally, 2 for a "someone else" contact.
function PersonFieldsEditor({
  role,
  testidPrefix,
  person,
  onChange,
  includeDob = true,
  includeBankruptcyAndCapacity,
  focus,
  focusPrefix,
  afterLastName,
  addressLines = 3,
}) {
  const address = person.address || {};
  const lines = address.lines || [];
  const fieldFocus = (field) => focus?.(focusPrefix ? `${focusPrefix}.${field}` : `${testidPrefix}.${field}`);

  function edit(patch) {
    onChange({ ...person, ...patch });
  }

  function editAddressLine(index, value) {
    const nextLines = [lines[0] || "", lines[1] || "", lines[2] || ""];
    nextLines[index] = value;
    edit({ address: { ...address, lines: nextLines.filter((l) => l.trim() !== "") } });
  }

  const gap = { marginTop: 10 };

  return (
    <>
      <LpaQuestion qid={`lpa.${role}.title`} label="Title">
        <input
          data-testid={`${testidPrefix}-title`}
          value={person.title || ""}
          onChange={(e) => edit({ title: e.target.value })}
          onFocus={() => fieldFocus("title")}
          style={inputStyle}
        />
      </LpaQuestion>
      <LpaQuestion qid={`lpa.${role}.first_names`} label="First names" style={gap}>
        <input
          data-testid={`${testidPrefix}-first-names`}
          value={person.first_names || ""}
          onChange={(e) => edit({ first_names: e.target.value })}
          onFocus={() => fieldFocus("first_names")}
          style={inputStyle}
        />
      </LpaQuestion>
      <LpaQuestion qid={`lpa.${role}.last_name`} label="Last name" style={gap}>
        <input
          data-testid={`${testidPrefix}-last-name`}
          value={person.last_name || ""}
          onChange={(e) => edit({ last_name: e.target.value })}
          onFocus={() => fieldFocus("last_name")}
          style={inputStyle}
        />
      </LpaQuestion>
      {afterLastName}
      {includeDob && (
        <LpaQuestion qid={`lpa.${role}.date_of_birth`} label="Date of birth" style={gap}>
          <input
            data-testid={`${testidPrefix}-dob`}
            type="date"
            value={person.date_of_birth || ""}
            onChange={(e) => edit({ date_of_birth: e.target.value })}
            onFocus={() => fieldFocus("date_of_birth")}
            style={inputStyle}
          />
        </LpaQuestion>
      )}
      <LpaQuestion qid={`lpa.${role}.address`} label="Address" style={gap}>
        <label style={{ fontSize: 13, display: "block", marginBottom: 2 }}>Address line 1</label>
        <input
          data-testid={`${testidPrefix}-address-line1`}
          value={lines[0] || ""}
          onChange={(e) => editAddressLine(0, e.target.value)}
          onFocus={() => fieldFocus("address")}
          style={inputStyle}
        />
        <label style={{ fontSize: 13, marginTop: 8, display: "block", marginBottom: 2 }}>Address line 2 (optional)</label>
        <input
          data-testid={`${testidPrefix}-address-line2`}
          value={lines[1] || ""}
          onChange={(e) => editAddressLine(1, e.target.value)}
          onFocus={() => fieldFocus("address")}
          style={inputStyle}
        />
        {addressLines >= 3 && (
          <>
            <label style={{ fontSize: 13, marginTop: 8, display: "block", marginBottom: 2 }}>Address line 3 (optional)</label>
            <input
              data-testid={`${testidPrefix}-address-line3`}
              value={lines[2] || ""}
              onChange={(e) => editAddressLine(2, e.target.value)}
              onFocus={() => fieldFocus("address")}
              style={inputStyle}
            />
          </>
        )}
        <label style={{ fontSize: 13, marginTop: 8, display: "block", marginBottom: 2 }}>Postcode</label>
        <input
          data-testid={`${testidPrefix}-address-postcode`}
          value={address.postcode || ""}
          onChange={(e) => edit({ address: { ...address, postcode: e.target.value } })}
          onFocus={() => fieldFocus("address")}
          style={inputStyle}
        />
      </LpaQuestion>
      {includeBankruptcyAndCapacity && (
        <>
          <LpaQuestion
            qid="lpa.attorney.bankruptcy_status"
            label="Is this attorney currently bankrupt or subject to a debt relief order?"
            style={{ marginTop: 12 }}
            labelStyle={{ marginBottom: 6 }}
          >
            <YesNo
              testidPrefix={`${testidPrefix}-bankruptcy`}
              value={person.bankruptcy_status}
              onPick={(val) => edit({ bankruptcy_status: val })}
            />
          </LpaQuestion>
          <LpaQuestion
            qid="lpa.attorney.capacity_declared"
            label="Has this attorney declared they have the capacity to act?"
            style={{ marginTop: 12 }}
            labelStyle={{ marginBottom: 6 }}
          >
            <YesNo
              testidPrefix={`${testidPrefix}-capacity`}
              value={person.capacity_declared ?? true}
              onPick={(val) => edit({ capacity_declared: val })}
            />
          </LpaQuestion>
        </>
      )}
    </>
  );
}

export const LPA_SECTIONS = [
  { id: "s1_donor", label: "Section 1 - The donor", opg: "Your details (LP1H/F section 1)" },
  { id: "s2_replacement", label: "Section 2 - Replacement or new LPA", opg: "Are you replacing an existing LPA (LP1H/F section 2)" },
  { id: "s3_attorneys", label: "Section 3 - The attorneys", opg: "The people you choose to be your attorneys (LP1H/F section 3)" },
  { id: "s4_replacements", label: "Section 4 - Replacement attorneys", opg: "Replacement attorneys (LP1H/F section 4)" },
  { id: "s5_when_act", label: "Section 5 - When attorneys can act", opg: "How the attorneys are authorised to act (LP1H section 5 / LP1F section 5)" },
  { id: "s6_preferences", label: "Section 6 - Preferences", opg: "Preferences (LP1H/F section 7 A)" },
  { id: "s7_instructions", label: "Section 7 - Instructions", opg: "Instructions (LP1H/F section 7 B)" },
  { id: "s8_notified", label: "Section 8 - Notified people", opg: "People to tell (LP1H/F section 6)" },
  { id: "s9_certificate", label: "Section 9 - Certificate provider", opg: "Certificate provider (LP1H/F section 10)" },
  { id: "s10_review", label: "Section 10 - Review and submit", opg: "Review before signing" },
];

const inputStyle = {
  width: "100%",
  padding: 10,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  fontFamily: "inherit",
  fontSize: 14,
};
const textareaStyle = { ...inputStyle, minHeight: 100, resize: "vertical" };

const removeButtonStyle = { marginTop: 12, padding: "6px 14px", borderRadius: 999, border: "1px solid #cbd5e1", background: "#fff", color: "#334155", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const addButtonStyle = { padding: "8px 16px", borderRadius: 8, border: "1px solid #4338ca", background: "#4338ca", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const cardStyle = { border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 14 };
const radioRowStyle = { display: "flex", alignItems: "center", gap: 8, fontSize: 13 };
const boxedOptionStyle = (active) => ({
  display: "block",
  padding: 10,
  marginBottom: 6,
  border: active ? "2px solid #4338ca" : "1px solid #cbd5e1",
  borderRadius: 8,
  cursor: "pointer",
  background: active ? "#eef2ff" : "#fff",
  flex: 1,
});

function useLocalState(initial) {
  const [state, setState] = useState(initial || {});
  return [state, setState];
}

// eslint-disable-next-line react/prop-types
export default function LPAFormSections({
  activeSection,
  defaultInstrument,
  onInstrumentChange,
  onFocusField,
  reviewPanel,
  capacityPanel,
  lpaKind,
}) {
  const [instrument, setInstrument] = useLocalState(defaultInstrument || {});

  // BM-H3414 - previously updated render state via one setState call and
  // computed the onInstrumentChange payload via a second, setTimeout(0)
  // -deferred setState call. Two back-to-back trigger() calls (e.g.
  // first_names then last_name) could have the second call's deferred
  // callback read a "prev" that had not yet absorbed the first call's
  // update, dropping it from the payload the parent receives - the exact
  // failure a multi-field-edit test caught. One synchronous update, used
  // for both render state and the callback, has no such race. The parent
  // (ClientFormLPA.jsx) already debounces at 700ms, so nothing here was
  // ever doing useful debouncing.
  const trigger = useCallback(
    (path, val) => {
      setInstrument((prev) => {
        const next = { ...prev };
        // Two-level path only (e.g., "donor.full_name"); good enough for LPA sections
        const [head, tail] = path.split(".");
        if (tail === undefined) next[head] = val;
        else next[head] = { ...(prev[head] || {}), [tail]: val };
        onInstrumentChange?.(next);
        return next;
      });
    },
    [setInstrument, onInstrumentChange]
  );

  const focus = (fieldId) => onFocusField?.(fieldId);

  // BM-H3545 - found live in a real browser click-through: replacement_mode
  // is required by the engine schema (CommonInstrument.replacement_mode,
  // no .default()/.nullish()) on EVERY instrument, but the question that
  // sets it only renders once replacements.length > 0 ("if more than one
  // attorney needs replacing..."). A donor who adds zero replacement
  // attorneys - a normal, common choice - had no way to ever satisfy this
  // field, so the engine could never parse their instrument and submit
  // could never succeed. default_all_at_once is a genuinely harmless
  // default when there are no replacements to order at all; it is set
  // once here, independent of which section is on screen, rather than
  // waiting for the client to visit a question that would not be shown.
  useEffect(() => {
    if (!instrument.replacement_mode) {
      trigger("replacement_mode", { type: "default_all_at_once" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrument.replacement_mode]);

  if (activeSection === "s1_donor") {
    const donor = instrument.donor || {};

    function editDonor(patch) {
      const next = { ...donor, ...patch };
      if (!next.id) next.id = newId("donor");
      trigger("donor", next);
    }

    return (
      <section data-testid="lpa-section-s1-donor">
        <h2>{LPA_SECTIONS[0].label}</h2>
        <p style={{ fontSize: 13, color: "#334155" }}>{LPA_SECTIONS[0].opg}</p>
        <LpaIntro id="s1_donor" />

        <PersonFieldsEditor
          role="donor"
          testidPrefix="lpa-donor"
          person={donor}
          onChange={editDonor}
          focus={focus}
          focusPrefix="donor"
          afterLastName={
            <LpaQuestion qid="lpa.donor.other_names" label="Other names you have been known by (optional)" style={{ marginTop: 10 }}>
              <input
                data-testid="lpa-donor-other-names"
                value={donor.other_names || ""}
                onChange={(e) => editDonor({ other_names: e.target.value || null })}
                onFocus={() => focus("donor.other_names")}
                style={inputStyle}
              />
            </LpaQuestion>
          }
        />

        <LpaQuestion qid="lpa.donor.email" label="Email (optional)" style={{ marginTop: 12 }}>
          <input
            data-testid="lpa-donor-email"
            type="email"
            value={donor.email || ""}
            onChange={(e) => editDonor({ email: e.target.value || null })}
            onFocus={() => focus("donor.email")}
            style={inputStyle}
          />
        </LpaQuestion>

        <LpaQuestion qid="lpa.donor.phone" label="Phone (optional)" style={{ marginTop: 12 }}>
          <input
            data-testid="lpa-donor-phone"
            value={donor.phone || ""}
            onChange={(e) => editDonor({ phone: e.target.value || null })}
            onFocus={() => focus("donor.phone")}
            style={inputStyle}
          />
        </LpaQuestion>

        <LpaQuestion qid="lpa.donor.welsh_correspondence" label="Should OPG correspond with you in Welsh?" style={{ marginTop: 16 }} labelStyle={{ marginBottom: 6 }}>
          <YesNo
            testidPrefix="lpa-donor-welsh-correspondence"
            value={donor.welsh_correspondence}
            onPick={(val) => editDonor({ welsh_correspondence: val })}
          />
        </LpaQuestion>

        <LpaQuestion
          qid="lpa.donor.has_will_in_place"
          label="Not an OPG question, but needed for your file: does the donor have a Will in place?"
          style={{ marginTop: 16 }}
          labelStyle={{ marginBottom: 6 }}
        >
          <YesNo
            testidPrefix="lpa-donor-has-will"
            value={donor.has_will_in_place}
            onPick={(val) => editDonor({ has_will_in_place: val })}
          />
        </LpaQuestion>
      </section>
    );
  }

  if (activeSection === "s3_attorneys") {
    const attorneys = instrument.attorneys || [];

    function setAttorneys(next) {
      trigger("attorneys", next);
    }

    function addAttorney() {
      setAttorneys([...attorneys, { id: newId("attorney"), capacity_declared: true }]);
    }

    function updateAttorney(index, nextPerson) {
      setAttorneys(attorneys.map((a, i) => (i === index ? nextPerson : a)));
    }

    function removeAttorney(index) {
      setAttorneys(attorneys.filter((_, i) => i !== index));
    }

    const decisionMode = instrument.decision_mode || {};

    function setDecisionModeType(type) {
      if (type === "mixed") trigger("decision_mode", { type, joint_decisions: decisionMode.joint_decisions || "" });
      else trigger("decision_mode", { type });
    }

    // BM-H3669 - the option wording is unchanged. The tooltip id for each
    // option is lpa.attorney.decision_mode_<key>.
    const DECISION_MODE_OPTIONS = [
      ["sole", "One attorney acts alone"],
      ["jointly", "All attorneys must agree on every decision"],
      ["jointly_and_severally", "Attorneys may act together or separately"],
      ["mixed", "Some decisions must be joint, others separate"],
    ];

    return (
      <section data-testid="lpa-section-s3-attorneys">
        <h2>{LPA_SECTIONS[2].label}</h2>
        <p style={{ fontSize: 13, color: "#334155" }}>{LPA_SECTIONS[2].opg}</p>
        <LpaIntro id="s3_attorneys" />
        <LpaQuestion qid="lpa.attorney.who" label="Your attorneys (compulsory: at least one)" labelStyle={{ marginBottom: 8 }}>
          {attorneys.map((attorney, i) => (
            <div key={attorney.id || i} data-testid={`lpa-attorney-${i}`} style={cardStyle}>
              <PersonFieldsEditor
                role="attorney"
                testidPrefix={`lpa-attorney-${i}`}
                person={attorney}
                onChange={(next) => updateAttorney(i, next)}
                includeBankruptcyAndCapacity
                focus={focus}
              />
              <button
                type="button"
                data-testid={`lpa-attorney-${i}-remove`}
                onClick={() => removeAttorney(i)}
                style={removeButtonStyle}
              >
                Remove this attorney
              </button>
            </div>
          ))}
          <button
            type="button"
            data-testid="lpa-attorneys-add"
            onClick={addAttorney}
            style={addButtonStyle}
          >
            Add an attorney
          </button>
        </LpaQuestion>

        {attorneys.length > 0 && (
          <LpaQuestion
            qid="lpa.attorney.decision_mode"
            label="If you appoint more than one attorney, how should they make decisions? (compulsory)"
            style={{ marginTop: 20 }}
            labelStyle={{ marginBottom: 6 }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {DECISION_MODE_OPTIONS.map(([key, label]) => (
                <LpaOption key={key} qid={`lpa.attorney.decision_mode_${key}`} label={label} labelStyle={radioRowStyle}>
                  <input
                    type="radio"
                    name="decision-mode"
                    data-testid={`lpa-decision-mode-${key}`}
                    checked={decisionMode.type === key}
                    onChange={() => setDecisionModeType(key)}
                  />
                  {label}
                </LpaOption>
              ))}
            </div>
            {decisionMode.type === "mixed" && (
              <LpaQuestion qid="lpa.attorney.mixed_decisions" label="Describe which decisions must be joint" style={{ marginTop: 10 }}>
                <textarea
                  data-testid="lpa-decision-mode-mixed-text"
                  value={decisionMode.joint_decisions || ""}
                  onChange={(e) => trigger("decision_mode", { type: "mixed", joint_decisions: e.target.value })}
                  style={textareaStyle}
                />
              </LpaQuestion>
            )}
          </LpaQuestion>
        )}
      </section>
    );
  }

  if (activeSection === "s4_replacements") {
    const replacements = instrument.replacements || [];
    const replacementMode = instrument.replacement_mode || {};

    function setReplacements(next) {
      trigger("replacements", next);
    }

    function addReplacement() {
      setReplacements([...replacements, { id: newId("replacement") }]);
    }

    function updateReplacement(index, nextPerson) {
      setReplacements(replacements.map((r, i) => (i === index ? nextPerson : r)));
    }

    function removeReplacement(index) {
      setReplacements(replacements.filter((_, i) => i !== index));
    }

    function setReplacementModeType(type) {
      if (type === "custom") trigger("replacement_mode", { type, custom_specification: replacementMode.custom_specification || "" });
      else trigger("replacement_mode", { type });
    }

    return (
      <section data-testid="lpa-section-s4-replacements">
        <h2>{LPA_SECTIONS[3].label}</h2>
        <p style={{ fontSize: 13, color: "#334155" }}>{LPA_SECTIONS[3].opg}</p>
        <LpaIntro id="s4_replacements" />
        <LpaQuestion qid="lpa.replacement.who" label="Your replacement attorneys (optional)" labelStyle={{ marginBottom: 8 }}>
          {replacements.map((replacement, i) => (
            <div key={replacement.id || i} data-testid={`lpa-replacement-${i}`} style={cardStyle}>
              <PersonFieldsEditor
                role="replacement"
                testidPrefix={`lpa-replacement-${i}`}
                person={replacement}
                onChange={(next) => updateReplacement(i, next)}
                includeDob
                focus={focus}
              />
              <button
                type="button"
                data-testid={`lpa-replacement-${i}-remove`}
                onClick={() => removeReplacement(i)}
                style={removeButtonStyle}
              >
                Remove this replacement
              </button>
            </div>
          ))}
          <button
            type="button"
            data-testid="lpa-replacements-add"
            onClick={addReplacement}
            style={addButtonStyle}
          >
            Add a replacement attorney
          </button>
        </LpaQuestion>

        {replacements.length > 0 && (
          <LpaQuestion
            qid="lpa.replacement.mode"
            label="If more than one attorney needs replacing, how should replacements step in?"
            style={{ marginTop: 20 }}
            labelStyle={{ marginBottom: 6 }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {/* BM-H3669 - label corrected, solicitor to confirm. It read
                  "All replacements step in together, in the order listed",
                  which contradicts itself. The OPG guide LP12 says they all
                  step in at the same time, and that an order can only be stated
                  when the attorneys act jointly and severally. The test id and
                  the value written (default_all_at_once) are unchanged. */}
              <LpaOption qid="lpa.replacement.mode_default_all_at_once" label="All replacements step in at the same time" labelStyle={radioRowStyle}>
                <input
                  type="radio"
                  name="replacement-mode"
                  data-testid="lpa-replacement-mode-default_all_at_once"
                  checked={replacementMode.type === "default_all_at_once"}
                  onChange={() => setReplacementModeType("default_all_at_once")}
                />
                All replacements step in at the same time
              </LpaOption>
              <LpaOption qid="lpa.replacement.mode_custom" label="Something else (describe below)" labelStyle={radioRowStyle}>
                <input
                  type="radio"
                  name="replacement-mode"
                  data-testid="lpa-replacement-mode-custom"
                  checked={replacementMode.type === "custom"}
                  onChange={() => setReplacementModeType("custom")}
                />
                Something else (describe below)
              </LpaOption>
            </div>
            {replacementMode.type === "custom" && (
              <LpaQuestion qid="lpa.replacement.custom_text" label="Describe how replacements should step in" style={{ marginTop: 10 }}>
                <textarea
                  data-testid="lpa-replacement-mode-custom-text"
                  value={replacementMode.custom_specification || ""}
                  onChange={(e) => trigger("replacement_mode", { type: "custom", custom_specification: e.target.value })}
                  style={textareaStyle}
                />
              </LpaQuestion>
            )}
          </LpaQuestion>
        )}
      </section>
    );
  }

  if (activeSection === "s8_notified") {
    const notifiedPersons = instrument.notified_persons || [];

    function setNotifiedPersons(next) {
      trigger("notified_persons", next);
    }

    function addNotifiedPerson() {
      setNotifiedPersons([...notifiedPersons, { id: newId("notified") }]);
    }

    function updateNotifiedPerson(index, nextPerson) {
      setNotifiedPersons(notifiedPersons.map((n, i) => (i === index ? nextPerson : n)));
    }

    function removeNotifiedPerson(index) {
      setNotifiedPersons(notifiedPersons.filter((_, i) => i !== index));
    }

    return (
      <section data-testid="lpa-section-s8-notified">
        <h2>{LPA_SECTIONS[7].label}</h2>
        <p style={{ fontSize: 13, color: "#334155" }}>{LPA_SECTIONS[7].opg}</p>
        <LpaIntro id="s8_notified" />
        <LpaQuestion qid="lpa.notified.who" label="People to tell (optional, up to five)" labelStyle={{ marginBottom: 8 }}>
          {notifiedPersons.map((person, i) => (
            <div key={person.id || i} data-testid={`lpa-notified-${i}`} style={cardStyle}>
              <PersonFieldsEditor
                role="notified"
                testidPrefix={`lpa-notified-${i}`}
                person={person}
                onChange={(next) => updateNotifiedPerson(i, next)}
                includeDob={false}
                focus={focus}
              />
              <button
                type="button"
                data-testid={`lpa-notified-${i}-remove`}
                onClick={() => removeNotifiedPerson(i)}
                style={removeButtonStyle}
              >
                Remove
              </button>
            </div>
          ))}
          {notifiedPersons.length < 5 && (
            <button
              type="button"
              data-testid="lpa-notified-add"
              onClick={addNotifiedPerson}
              style={addButtonStyle}
            >
              Add a person to tell
            </button>
          )}
        </LpaQuestion>
      </section>
    );
  }

  if (activeSection === "s5_when_act") {
    return (
      <section data-testid="lpa-section-s5-when-act">
        <h2>{LPA_SECTIONS[4].label}</h2>
        <p style={{ fontSize: 13, color: "#334155" }}>{LPA_SECTIONS[4].opg}</p>
        {/* The ids use an underscore, not a dot, so the Wills tooltip gate in
            scripts/preflight_release_gates.py (which reads s<digits>_name.field
            strings as Wills question ids) does not mistake them for one. */}
        <LpaIntro id={lpaKind === "hw" ? "s5_when_act_hw" : "s5_when_act_pf"} />
        {lpaKind === "hw" ? (
          <LpaQuestion
            qid="lpa.lst.choice"
            label="Life-sustaining treatment: Option A or Option B (compulsory)"
            labelStyle={{ marginBottom: 8, fontSize: 14 }}
          >
            {/* BM-H3545 - wrote the flat instrument.life_sustaining_option
                ("A"/"B"), which the engine's LPAHW schema has never read.
                The real field is the nested life_sustaining_treatment.choice,
                with values "option_a"/"option_b". signed_at_same_time_as_s9
                defaults true on the engine side and is left unset here;
                life_sustaining_treatment.witness is signing-stage data and
                stays deferred with the rest of signature_bundle. */}
            {[
              ["option_a", "A"],
              ["option_b", "B"],
            ].map(([value, label]) => {
              const active = instrument.life_sustaining_treatment?.choice === value;
              return (
                <LpaOption
                  key={value}
                  qid={`lpa.lst.${value}`}
                  label={`Option ${label}`}
                  labelTestId={`lpa-lst-option-${label}`}
                  labelStyle={boxedOptionStyle(active)}
                  style={{ marginBottom: 0 }}
                >
                  <input
                    type="radio"
                    name="lst"
                    checked={active}
                    onChange={() => trigger("life_sustaining_treatment.choice", value)}
                    style={{ marginRight: 8 }}
                  />
                  <strong>Option {label}:</strong>{" "}
                  {label === "A"
                    ? "My attorneys can give or refuse consent to life-sustaining treatment on my behalf."
                    : "My attorneys can NOT give or refuse consent to life-sustaining treatment on my behalf."}
                </LpaOption>
              );
            })}
          </LpaQuestion>
        ) : (
          <LpaQuestion
            qid="lpa.when_act.choice"
            label="When may attorneys act? (compulsory)"
            labelStyle={{ marginBottom: 8, fontSize: 14 }}
          >
            {/* BM-H3545 - wrote the flat instrument.when_act_option with
                values this schema has never had. The real field is the
                top-level when_attorneys_can_act enum:
                "only_on_incapacity" / "on_registration_and_incapacity". */}
            {[
              ["only_on_incapacity", "Only when I do not have mental capacity to make the decision myself."],
              ["on_registration_and_incapacity", "As soon as the LPA is registered (and also if I later lose capacity)."],
            ].map(([value, label]) => {
              const active = instrument.when_attorneys_can_act === value;
              return (
                <LpaOption
                  key={value}
                  qid={`lpa.when_act.${value}`}
                  label={label}
                  labelTestId={`lpa-when-act-${value}`}
                  labelStyle={boxedOptionStyle(active)}
                >
                  <input
                    type="radio"
                    name="when-act"
                    checked={active}
                    onChange={() => trigger("when_attorneys_can_act", value)}
                    style={{ marginRight: 8 }}
                  />
                  {label}
                </LpaOption>
              );
            })}
          </LpaQuestion>
        )}
      </section>
    );
  }

  if (activeSection === "s6_preferences") {
    // BM-H3545 - wrote the flat instrument.preferences, which the engine's
    // PreferencesInstructions schema has never read. The real field is
    // preferences_and_instructions.preferences.
    return (
      <section data-testid="lpa-section-s6-preferences">
        <h2>{LPA_SECTIONS[5].label}</h2>
        <p style={{ fontSize: 13, color: "#334155" }}>{LPA_SECTIONS[5].opg}</p>
        <LpaIntro id="s6_preferences" />
        <p style={{ fontSize: 13, color: "#334155", background: "#fef3c7", padding: 10, borderRadius: 6 }}>
          Any wording you enter here is parked as a draft for your fee earner to review before it goes into the final LPA.
        </p>
        <LpaQuestion qid="lpa.preferences.text" label="Your preferences (optional)">
          <textarea
            data-testid="lpa-preferences-text"
            value={instrument.preferences_and_instructions?.preferences || ""}
            onChange={(e) => trigger("preferences_and_instructions.preferences", e.target.value)}
            onFocus={() => focus("preferences_and_instructions.preferences")}
            style={{ ...textareaStyle, minHeight: 160 }}
          />
        </LpaQuestion>
      </section>
    );
  }

  if (activeSection === "s7_instructions") {
    // BM-H3545 - same fix: the real field is
    // preferences_and_instructions.instructions, not a flat instructions key.
    return (
      <section data-testid="lpa-section-s7-instructions">
        <h2>{LPA_SECTIONS[6].label}</h2>
        <p style={{ fontSize: 13, color: "#334155" }}>{LPA_SECTIONS[6].opg}</p>
        <LpaIntro id="s7_instructions" />
        <p style={{ fontSize: 13, color: "#334155", background: "#fef3c7", padding: 10, borderRadius: 6 }}>
          Any wording you enter here is parked as a draft for your fee earner to review before it goes into the final LPA.
        </p>
        <LpaQuestion qid="lpa.instructions.text" label="Your instructions (optional)">
          <textarea
            data-testid="lpa-instructions-text"
            value={instrument.preferences_and_instructions?.instructions || ""}
            onChange={(e) => trigger("preferences_and_instructions.instructions", e.target.value)}
            onFocus={() => focus("preferences_and_instructions.instructions")}
            style={{ ...textareaStyle, minHeight: 160 }}
          />
        </LpaQuestion>
      </section>
    );
  }

  if (activeSection === "s9_certificate") {
    const certProvider = instrument.cert_provider || {};
    const exclusions = certProvider.exclusions || {};
    const donor = instrument.donor || {};
    const capacityFlags = donor.capacity_flags || {};

    function editCertProvider(patch) {
      const next = { ...certProvider, ...patch };
      // BM-H3545 - unlike donor/attorney/replacement/notified-person,
      // cert_provider.id was never auto-generated anywhere, yet the
      // engine schema requires it (CertificateProvider.id, no default).
      if (!next.id) next.id = newId("cert_provider");
      // The outer exclusions key is also required with no default (each
      // of its 10 inner booleans defaults to false individually, but only
      // once the key itself exists) - previously only created once the
      // client clicked at least one of the 10 exclusion questions, which
      // a certificate provider with no exclusions at all would never do.
      if (!next.exclusions) next.exclusions = {};
      trigger("cert_provider", next);
    }

    function editExclusion(key, value) {
      editCertProvider({ exclusions: { ...exclusions, [key]: value } });
    }

    function editCapacityFlag(key, value) {
      trigger("donor", { ...donor, capacity_flags: { ...capacityFlags, [key]: value } });
    }

    // BM-H3669 - the question wording is unchanged. The tooltip id for each is
    // lpa.cert_provider.excl_<key>.
    const EXCLUSION_QUESTIONS = [
      ["is_attorney_this_lpa", "Are they an attorney on this LPA?"],
      ["is_attorney_other_lpa_or_epa_for_donor", "Are they an attorney on another LPA or EPA for this donor?"],
      ["is_replacement_this_lpa", "Are they a replacement attorney on this LPA?"],
      ["is_family_of_donor", "Are they a family member of the donor?"],
      ["is_family_of_attorney", "Are they a family member of an attorney?"],
      ["is_unmarried_partner_of_donor", "Are they the donor's unmarried partner?"],
      ["is_unmarried_partner_of_attorney", "Are they an attorney's unmarried partner?"],
      ["is_business_partner_of_donor_or_attorney", "Are they a business partner of the donor or an attorney?"],
      ["is_employee_of_donor_or_attorney", "Are they an employee of the donor or an attorney?"],
      ["is_care_home_stakeholder", "Do they own, manage or work at the donor's care home?"],
    ];

    const CAPACITY_FLAG_QUESTIONS = [
      ["medical_diagnosis_disclosed", "Has the donor disclosed a medical diagnosis relevant to capacity (e.g. dementia, stroke history, learning disability, severe mental illness)?"],
      ["family_dispute_indicator", "Is there a family dispute that could affect this LPA?"],
      ["communication_impairment", "Does the donor need an interpreter, or have a hearing impairment or language barrier affecting the call?"],
      ["age_over_85_no_recent_medical", "Is the donor over 85 with no recent medical assessment on file?"],
      ["undue_pressure_indicator", "Was there any sign of undue pressure on the donor during the capacity conversation?"],
      ["capacity_challenge_history", "Has a previous LPA for this donor been rejected or revoked on capacity grounds, or was the Court of Protection involved?"],
    ];

    return (
      <section data-testid="lpa-section-s9-certificate">
        <h2>{LPA_SECTIONS[8].label}</h2>
        <p style={{ fontSize: 13, color: "#334155" }}>{LPA_SECTIONS[8].opg}</p>
        <LpaIntro id="s9_certificate" />

        <LpaQuestion qid="lpa.cert_provider.who" label="Your certificate provider (compulsory)" labelStyle={{ marginBottom: 8 }}>
          <PersonFieldsEditor
            role="cert_provider"
            testidPrefix="lpa-cert-provider"
            person={certProvider}
            onChange={editCertProvider}
            includeDob={false}
            focus={focus}
            focusPrefix="cert_provider"
          />
        </LpaQuestion>

        <LpaQuestion qid="lpa.cert_provider.basis" label="On what basis do they know the donor?" style={{ marginTop: 16 }} labelStyle={{ marginBottom: 6 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <LpaOption qid="lpa.cert_provider.basis_known_2_years" label="They have known the donor personally for at least 2 years" labelStyle={radioRowStyle}>
              <input
                type="radio"
                name="cert-provider-basis"
                data-testid="lpa-cert-provider-basis-known_2_years"
                checked={certProvider.basis === "known_2_years"}
                onChange={() => editCertProvider({ basis: "known_2_years" })}
              />
              They have known the donor personally for at least 2 years
            </LpaOption>
            <LpaOption qid="lpa.cert_provider.basis_professional_skills" label="They have relevant professional skills" labelStyle={radioRowStyle}>
              <input
                type="radio"
                name="cert-provider-basis"
                data-testid="lpa-cert-provider-basis-professional_skills"
                checked={certProvider.basis === "professional_skills"}
                onChange={() => editCertProvider({ basis: "professional_skills" })}
              />
              They have relevant professional skills (e.g. a solicitor or doctor)
            </LpaOption>
          </div>
        </LpaQuestion>

        {/* BM-H3669 - label corrected, solicitor to confirm. This heading read
            "MCA 2005 reg 8(2) excludes certain people from acting as
            certificate provider." The OPG guide LP12 gives the list of people
            who cannot be a certificate provider but cites no regulation, and the
            rules sit in the LPA Regulations 2007, so the citation is replaced
            with plain wording that names no regulation. The ten test ids and
            the values written are unchanged. */}
        <LpaQuestion
          qid="lpa.cert_provider.exclusions"
          label="Some people cannot act as a certificate provider. Please confirm each answer for your certificate provider:"
          style={{ marginTop: 16 }}
          labelStyle={{ marginBottom: 6 }}
        >
          {EXCLUSION_QUESTIONS.map(([key, label]) => (
            <LpaQuestion
              key={key}
              qid={`lpa.cert_provider.excl_${key}`}
              label={label}
              style={{ marginBottom: 8 }}
              labelStyle={{ fontWeight: 400 }}
            >
              <YesNo
                testidPrefix={`lpa-cert-provider-exclusion-${key}`}
                value={exclusions[key]}
                onPick={(val) => editExclusion(key, val)}
                compact
              />
            </LpaQuestion>
          ))}
        </LpaQuestion>

        <LpaQuestion
          qid="lpa.cert_provider.days_to_availability"
          label="How many days until this person is available to act as certificate provider?"
          style={{ marginTop: 10 }}
        >
          <input
            data-testid="lpa-cert-provider-days-to-availability"
            type="number"
            min="0"
            value={certProvider.days_to_availability ?? ""}
            onChange={(e) => editCertProvider({ days_to_availability: e.target.value === "" ? null : Number(e.target.value) })}
            onFocus={() => focus("cert_provider.days_to_availability")}
            style={inputStyle}
          />
        </LpaQuestion>

        {/* BM-H3414 - these drive the engine's V-CAP01-06 pre-certification
            risk gate (donor.capacity_flags), which is distinct from the
            MCA s.2/s.3 functional capacity checklist below
            (capacityPanel, instrument.capacity_review) - that checklist
            is not read by the engine and is kept as-is. */}
        <LpaQuestion
          qid="lpa.capacity_flags.group"
          label="Before certification: any of the following apply?"
          style={{ marginTop: 20 }}
          labelStyle={{ marginBottom: 6 }}
        >
          {CAPACITY_FLAG_QUESTIONS.map(([key, label]) => (
            <LpaQuestion
              key={key}
              qid={`lpa.capacity_flags.${key}`}
              label={label}
              style={{ marginBottom: 8 }}
              labelStyle={{ fontWeight: 400 }}
            >
              <YesNo
                testidPrefix={`lpa-capacity-flag-${key}`}
                value={capacityFlags[key]}
                onPick={(val) => editCapacityFlag(key, val)}
                compact
              />
            </LpaQuestion>
          ))}
        </LpaQuestion>

        <div style={{ marginTop: 20 }}>
          {capacityPanel}
        </div>
      </section>
    );
  }

  if (activeSection === "s10_review") {
    const attorneys = instrument.attorneys || [];
    const applicant = instrument.applicant || {};
    const correspondent = instrument.correspondent || {};
    const correspondentPrefs = correspondent.contact_prefs || {};
    const feeRoute = instrument.fee_route || {};

    function editContactPrefs(patch) {
      trigger("correspondent", { ...correspondent, contact_prefs: { ...correspondentPrefs, ...patch } });
    }

    function setApplicantType(type) {
      if (type === "attorneys") trigger("applicant", { type, attorney_signatures_required: applicant.attorney_signatures_required || [] });
      else trigger("applicant", { type });
    }

    function toggleAttorneySignature(attorneyId, checked) {
      const current = applicant.attorney_signatures_required || [];
      const next = checked ? [...current, attorneyId] : current.filter((id) => id !== attorneyId);
      trigger("applicant", { type: "attorneys", attorney_signatures_required: next });
    }

    function setCorrespondentType(type) {
      if (type === "donor") trigger("correspondent", { type, contact_prefs: correspondentPrefs });
      else if (type === "attorney") trigger("correspondent", { type, attorney_id: correspondent.attorney_id || null, contact_prefs: correspondentPrefs });
      else trigger("correspondent", { type, title: correspondent.title || "", first_names: correspondent.first_names || "", last_name: correspondent.last_name || "", company: correspondent.company || null, address: correspondent.address || {}, contact_prefs: correspondentPrefs });
    }

    function editCorrespondentOther(patch) {
      trigger("correspondent", { ...correspondent, type: "other", ...patch });
    }

    function setFeeRouteType(type) {
      trigger("fee_route", { type });
    }

    function editFeeRoute(patch) {
      trigger("fee_route", { ...feeRoute, ...patch });
    }

    // BM-H3669 - label corrected, solicitor to confirm: the repeat option read
    // "This is a repeat application (reduced fee)". The OPG guide LP12 does not
    // say a repeat application has a reduced fee. It says you can apply again
    // within three months, quoting the case number from the letter that came
    // with the returned application. The words "(reduced fee)" are removed.
    // The test id and the value written (repeat) are unchanged.
    const FEE_ROUTES = [
      ["full", "Pay the full fee"],
      ["remission", "Apply for a fee remission (reduced fee, income-based)"],
      ["exemption", "Apply for a fee exemption (means-tested benefits)"],
      ["hardship", "Apply on grounds of financial hardship"],
      ["repeat", "This is a repeat application"],
    ];

    return (
      <section data-testid="lpa-section-s10-review">
        <h2>{LPA_SECTIONS[9].label}</h2>
        <LpaIntro id="s10_review" />

        <LpaQuestion qid="lpa.applicant.who" label="Who is applying to register this LPA?" style={{ marginBottom: 20 }} labelStyle={{ marginBottom: 6 }}>
          <LpaOption qid="lpa.applicant.donor" label="The donor" labelStyle={radioRowStyle} style={{ marginBottom: 6 }}>
            <input type="radio" name="applicant" data-testid="lpa-applicant-donor" checked={applicant.type === "donor"} onChange={() => setApplicantType("donor")} />
            The donor
          </LpaOption>
          <LpaOption qid="lpa.applicant.attorneys" label="The attorneys" labelStyle={radioRowStyle}>
            <input type="radio" name="applicant" data-testid="lpa-applicant-attorneys" checked={applicant.type === "attorneys"} onChange={() => setApplicantType("attorneys")} />
            The attorneys
          </LpaOption>
          {applicant.type === "attorneys" && (
            <LpaQuestion
              qid="lpa.applicant.signatures"
              label="Which attorneys will sign the application?"
              style={{ marginTop: 8, paddingLeft: 20 }}
              labelStyle={{ fontWeight: 400, color: "#334155" }}
            >
              {attorneys.map((a) => (
                <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    data-testid={`lpa-applicant-signature-${a.id}`}
                    checked={(applicant.attorney_signatures_required || []).includes(a.id)}
                    onChange={(e) => toggleAttorneySignature(a.id, e.target.checked)}
                  />
                  {a.first_names || "(unnamed attorney)"} {a.last_name || ""}
                </label>
              ))}
            </LpaQuestion>
          )}
        </LpaQuestion>

        <LpaQuestion qid="lpa.correspondent.who" label="Who should OPG contact about this application?" style={{ marginBottom: 20 }} labelStyle={{ marginBottom: 6 }}>
          <LpaOption qid="lpa.correspondent.donor" label="The donor" labelStyle={radioRowStyle} style={{ marginBottom: 6 }}>
            <input type="radio" name="correspondent" data-testid="lpa-correspondent-donor" checked={correspondent.type === "donor"} onChange={() => setCorrespondentType("donor")} />
            The donor
          </LpaOption>
          <LpaOption qid="lpa.correspondent.attorney" label="An attorney" labelStyle={radioRowStyle} style={{ marginBottom: 6 }}>
            <input type="radio" name="correspondent" data-testid="lpa-correspondent-attorney" checked={correspondent.type === "attorney"} onChange={() => setCorrespondentType("attorney")} />
            An attorney
          </LpaOption>
          {correspondent.type === "attorney" && (
            // BM-H3710: the engine needs to know WHICH attorney. Without this the
            // choice was saved as attorney_id null and the questionnaire could not
            // be submitted.
            <LpaQuestion
              qid="lpa.correspondent.which_attorney"
              label="Which attorney should OPG contact?"
              style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}
              labelStyle={{ fontWeight: 400, color: "#334155" }}
            >
              <div data-testid="lpa-correspondent-attorney-picker">
                {attorneys.length === 0 && (
                  <p style={{ fontSize: 13, color: "#334155" }}>Add your attorneys first (section 3), then come back and choose one.</p>
                )}
                {attorneys.map((a) => (
                  <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 4 }}>
                    <input
                      type="radio"
                      name="correspondent-attorney"
                      data-testid={`lpa-correspondent-attorney-${a.id}`}
                      checked={correspondent.attorney_id === a.id}
                      onChange={() => trigger("correspondent", { ...correspondent, type: "attorney", attorney_id: a.id })}
                    />
                    {a.first_names || "(unnamed attorney)"} {a.last_name || ""}
                  </label>
                ))}
              </div>
            </LpaQuestion>
          )}
          <LpaOption qid="lpa.correspondent.other" label="Someone else" labelStyle={radioRowStyle}>
            <input type="radio" name="correspondent" data-testid="lpa-correspondent-other" checked={correspondent.type === "other"} onChange={() => setCorrespondentType("other")} />
            Someone else
          </LpaOption>

          {correspondent.type === "other" && (
            <div style={{ marginTop: 8, paddingLeft: 20 }}>
              <PersonFieldsEditor
                role="correspondent"
                testidPrefix="lpa-correspondent-other"
                person={correspondent}
                onChange={editCorrespondentOther}
                includeDob={false}
                addressLines={2}
                afterLastName={
                  <LpaQuestion qid="lpa.correspondent.company" label="Company (optional)" style={{ marginTop: 10 }}>
                    <input data-testid="lpa-correspondent-other-company" value={correspondent.company || ""} onChange={(e) => editCorrespondentOther({ company: e.target.value || null })} style={inputStyle} />
                  </LpaQuestion>
                }
              />
            </div>
          )}

          {correspondent.type && (
            <LpaQuestion
              qid="lpa.correspondent.contact"
              label="How should OPG contact them?"
              style={{ marginTop: 10, paddingLeft: 20 }}
            >
              <LpaOption qid="lpa.correspondent.post" label="By post" labelStyle={radioRowStyle} style={{ marginBottom: 4 }}>
                <input type="checkbox" data-testid="lpa-correspondent-contact-post" checked={!!correspondentPrefs.post} onChange={(e) => editContactPrefs({ post: e.target.checked })} />
                By post
              </LpaOption>
              <LpaOption
                qid="lpa.correspondent.phone"
                label="By phone"
                labelStyle={radioRowStyle}
                style={{ marginBottom: 4 }}
                below={
                  correspondentPrefs.phone && (
                    <input data-testid="lpa-correspondent-contact-phone-number" aria-label="Phone number" value={correspondentPrefs.phone_number || ""} onChange={(e) => editContactPrefs({ phone_number: e.target.value || null })} placeholder="Phone number" style={{ ...inputStyle, marginBottom: 8 }} />
                  )
                }
              >
                <input type="checkbox" data-testid="lpa-correspondent-contact-phone" checked={!!correspondentPrefs.phone} onChange={(e) => editContactPrefs({ phone: e.target.checked })} />
                By phone
              </LpaOption>
              <LpaOption
                qid="lpa.correspondent.email"
                label="By email"
                labelStyle={radioRowStyle}
                style={{ marginBottom: 4 }}
                below={
                  correspondentPrefs.email && (
                    <input data-testid="lpa-correspondent-contact-email-address" aria-label="Email address" type="email" value={correspondentPrefs.email_address || ""} onChange={(e) => editContactPrefs({ email_address: e.target.value || null })} placeholder="Email address" style={{ ...inputStyle, marginBottom: 8 }} />
                  )
                }
              >
                <input type="checkbox" data-testid="lpa-correspondent-contact-email" checked={!!correspondentPrefs.email} onChange={(e) => editContactPrefs({ email: e.target.checked })} />
                By email
              </LpaOption>
              <LpaOption qid="lpa.correspondent.welsh" label="Correspond in Welsh" labelStyle={radioRowStyle}>
                <input type="checkbox" data-testid="lpa-correspondent-contact-welsh" checked={!!correspondentPrefs.welsh} onChange={(e) => editContactPrefs({ welsh: e.target.checked })} />
                Correspond in Welsh
              </LpaOption>
            </LpaQuestion>
          )}
        </LpaQuestion>

        <LpaQuestion qid="lpa.fee.route" label="How is the OPG registration fee being paid?" style={{ marginBottom: 20 }} labelStyle={{ marginBottom: 6 }}>
          {FEE_ROUTES.map(([key, label]) => (
            <LpaOption key={key} qid={`lpa.fee.${key}`} label={label} labelStyle={radioRowStyle} style={{ marginBottom: 6 }}>
              <input type="radio" name="fee-route" data-testid={`lpa-fee-route-${key}`} checked={feeRoute.type === key} onChange={() => setFeeRouteType(key)} />
              {label}
            </LpaOption>
          ))}

          {feeRoute.type === "remission" && (
            <div style={{ paddingLeft: 20 }}>
              <LpaQuestion qid="lpa.fee.remission_evidence" label="Has the donor's income evidence been provided?" labelStyle={{ marginBottom: 4 }}>
                <YesNo testidPrefix="lpa-fee-route-remission-evidence" value={feeRoute.evidence_declared} onPick={(val) => editFeeRoute({ evidence_declared: val })} compact capitalize={false} style={{ marginBottom: 8 }} />
              </LpaQuestion>
              <LpaQuestion qid="lpa.fee.remission_income" label="Donor's annual gross income (optional)">
                <input data-testid="lpa-fee-route-remission-income" type="number" min="0" value={feeRoute.donor_annual_gross_income ?? ""} onChange={(e) => editFeeRoute({ donor_annual_gross_income: e.target.value === "" ? null : Number(e.target.value) })} style={inputStyle} />
              </LpaQuestion>
            </div>
          )}
          {feeRoute.type === "exemption" && (
            <div style={{ paddingLeft: 20 }}>
              <LpaQuestion qid="lpa.fee.exemption_evidence" label="Has the benefits evidence been provided?" labelStyle={{ marginBottom: 4 }}>
                <YesNo testidPrefix="lpa-fee-route-exemption-evidence" value={feeRoute.evidence_declared} onPick={(val) => editFeeRoute({ evidence_declared: val })} compact capitalize={false} />
              </LpaQuestion>
            </div>
          )}
          {feeRoute.type === "hardship" && (
            <div style={{ paddingLeft: 20 }}>
              <LpaQuestion qid="lpa.fee.hardship_evidence" label="Has supporting evidence been provided?" labelStyle={{ marginBottom: 4 }}>
                <YesNo testidPrefix="lpa-fee-route-hardship-evidence" value={feeRoute.evidence_declared} onPick={(val) => editFeeRoute({ evidence_declared: val })} compact capitalize={false} style={{ marginBottom: 8 }} />
              </LpaQuestion>
              <LpaOption qid="lpa.fee.hardship_letter" label="Hardship letter included" labelStyle={radioRowStyle}>
                <input type="checkbox" data-testid="lpa-fee-route-hardship-letter" checked={!!feeRoute.hardship_letter_included} onChange={(e) => editFeeRoute({ hardship_letter_included: e.target.checked })} />
                Hardship letter included
              </LpaOption>
            </div>
          )}
          {feeRoute.type === "repeat" && (
            <div style={{ paddingLeft: 20 }}>
              <LpaQuestion qid="lpa.fee.repeat_case_number" label="Case number of the earlier application">
                <input data-testid="lpa-fee-route-repeat-case-number" value={feeRoute.repeat_case_number || ""} onChange={(e) => editFeeRoute({ repeat_case_number: e.target.value })} style={inputStyle} />
              </LpaQuestion>
              <LpaQuestion qid="lpa.fee.repeat_refusal_date" label="Date of the earlier refusal (optional)" style={{ marginTop: 10 }}>
                <input data-testid="lpa-fee-route-repeat-refusal-date" type="date" value={feeRoute.prior_refusal_date || ""} onChange={(e) => editFeeRoute({ prior_refusal_date: e.target.value || null })} style={inputStyle} />
              </LpaQuestion>
            </div>
          )}
        </LpaQuestion>

        <p style={{ fontSize: 13, color: "#334155" }}>
          Please review the live checks. Anything in red must be resolved before submission.
        </p>
        <div style={{ marginTop: 12 }}>{reviewPanel}</div>
      </section>
    );
  }

  // Section 2: lightweight stub (s3/s4/s8 have their own structured UI above)
  const stubMap = {
    s2_replacement: LPA_SECTIONS[1],
  };
  const cfg = stubMap[activeSection];
  if (!cfg) return null;
  return (
    <section data-testid={`lpa-section-${activeSection}`}>
      <h2>{cfg.label}</h2>
      <p style={{ fontSize: 13, color: "#334155" }}>{cfg.opg}</p>
      <LpaIntro id="s2_replacement" />
      <LpaQuestion qid="lpa.existing_lpa.notes" label="Notes about any earlier power of attorney (optional)">
        <textarea
          data-testid={`lpa-section-${activeSection}-notes`}
          value={instrument[activeSection] || ""}
          onChange={(e) => trigger(activeSection, e.target.value)}
          style={textareaStyle}
          placeholder="Free-form notes for this section (see fee earner for exact fields)"
        />
      </LpaQuestion>
    </section>
  );
}
